/**
 * 🔒 PROTECTED GUEST-FACING MODULE — DO NOT MODIFY WITHOUT EXPLICIT USER APPROVAL
 * ═══════════════════════════════════════════════════════════════════════════════
 * Locked under /app/memory/PROTECTED_GUEST_FACING.md (Feb 2026).
 * Read that file before proposing edits.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * InviteLandingPage — /invite/:token
 *
 * Glassmorphism invitation card with cinematic intro animation.
 * After the animation settles, headline text shines with a gold-etched
 * light-sweep effect — as if letters are engraved in polished gold.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InviteIntroAnimation from './InviteIntroAnimation';
import { resolveGuestGreeting } from '../lib/resolveGuestGreeting';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Colour tokens ─────────────────────────────────────────────────────── */
const C = {
  bg:          '#080d0d',
  outerBorder: 'rgba(212,168,83,0.70)',
  wave1:       'rgba(212,168,83,0.55)',
  wave2:       'rgba(212,168,83,0.30)',
  wave3:       'rgba(212,168,83,0.15)',
  glass:       'rgba(255,248,232,0.07)',
  glassBorder: 'rgba(212,168,83,0.95)',
  glassInner:  'rgba(212,168,83,0.55)',
  textPrimary: '#FFE88A',
  textSoft:    'rgba(255,255,255,0.92)',
  textFaint:   'rgba(255,255,255,0.72)',
  btnGoldA:    '#c9a84a',
  btnGoldB:    '#e8c86a',
  btnText:     '#000000',
};

export default function InviteLandingPage() {
  const { token }  = useParams();
  const navigate   = useNavigate();

  const [name, setName]           = useState('');
  const [eventDetails, setEvent]  = useState(null);
  const [loading, setLoading]     = useState(true);

  /* intro animation only plays when there is a token */
  const [showIntro, setShowIntro]     = useState(!!token);
  const [playShimmer, setPlayShimmer] = useState(false);

  const handleAnimationComplete = useCallback(() => {
    setShowIntro(false);
    setTimeout(() => setPlayShimmer(true), 150);
  }, []);

  /* ── Fetch guest name ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/parties/by-token/${token}`)
      .then(r => r.json())
      .then(d => {
        if (!d.found) return;
        const guests  = d.guests || [];
        const resolved = resolveGuestGreeting(
          guests,
          d.party?.party_name,
          d.primary_guest
        );
        setName(resolved);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Fetch event details ──────────────────────────────────────────────── */
  useEffect(() => {
    fetch(`${API}/api/public/guest-site-config`)
      .then(r => r.json())
      .then(d => setEvent(d.event_details || null))
      .catch(() => {});
  }, []);

  const handleViewInvitation = () => navigate(`/film/${token}`);
  const handleRsvpNow        = () => navigate(`/?token=${token}`);

  /* ── Format date ──────────────────────────────────────────────────────── */
  const formatDate = (raw) => {
    if (!raw) return 'SATURDAY, JULY 25TH 2026';
    try {
      const d     = new Date(raw);
      if (isNaN(d.getTime())) return raw.toUpperCase();
      const local = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);
      const days  = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
      const mons  = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE',
                     'JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];
      const day   = local.getDate();
      const sfx   = (Math.floor(day/10)===1) ? 'TH' : (['TH','ST','ND','RD'][day%10]||'TH');
      return `${days[local.getDay()]}, ${mons[local.getMonth()]} ${day}${sfx} ${local.getFullYear()}`;
    } catch(_) { return raw.toUpperCase(); }
  };

  const dateStr   = eventDetails?.date        ? formatDate(eventDetails.date) : 'SATURDAY, JULY 25TH 2026';
  const timeStr   = eventDetails?.time        || '12:00 PM';
  const venueName = eventDetails?.venue_name  || "SEASON'S CATERING";
  const venueAddr = eventDetails?.venue_address || '644 PASCACK RD, TOWNSHIP OF WASHINGTON, NJ 07676';

  /* ── Gold-etch style helpers ──────────────────────────────────────────────
     Metallic gradient mimics the depth of letters engraved in polished gold.
     The one-time glow animation fires when the card settles after the intro.
  ─────────────────────────────────────────────────────────────────────────── */
  const etchedGold = (delay = '0s') => playShimmer ? {
    background:           'linear-gradient(175deg, #FFF6C0 0%, #F0D060 20%, #C9A84A 50%, #E8C850 78%, #FFF6C0 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip:       'text',
    WebkitTextFillColor:  'transparent',
    color:                'transparent',
    animation:            `inv-etch-glow 3.2s ease-out ${delay} 1 both`,
  } : { color: C.textPrimary };

  /* ── Shared card styles ───────────────────────────────────────────────── */
  const cardStyle = {
    position:   'relative',
    width:      'clamp(300px, 92vw, 480px)',
    margin:     '0 auto',
    padding:    '2.5px',
    background: 'linear-gradient(145deg, #FFF0A0 0%, rgba(212,168,83,0.90) 20%, #C9A84A 50%, rgba(212,168,83,0.90) 80%, #FFF0A0 100%)',
    borderRadius: '3px',
    boxShadow:  '0 24px 80px rgba(0,0,0,0.7), 0 0 80px rgba(212,168,83,0.08)',
    animation:  playShimmer ? 'inv-card-settle 0.6s cubic-bezier(0.22,1,0.36,1) forwards' : undefined,
  };

  const innerCardStyle = {
    position:             'relative',
    overflow:             'hidden',
    background:           'rgba(20,15,8,0.58)',
    borderRadius:         '2px',
    padding:              '0',
  };

  const glassPanelStyle = {
    position:             'relative',
    margin:               '20px 20px 20px 20px',
    padding:              '28px 22px 22px',
    background:           'rgba(20,15,8,0.58)',
    backdropFilter:       'blur(26px) saturate(1.5)',
    WebkitBackdropFilter: 'blur(26px) saturate(1.5)',
    border:               '1px solid rgba(255,220,90,0.98)',
    boxShadow: [
      'inset 0 1.5px 0 rgba(255,220,100,0.22)',
      `inset 0 0 0 1px ${C.glassInner}`,
      '0 8px 32px rgba(0,0,0,0.45)',
      '0 0 4px rgba(255,210,60,0.4)',
    ].join(', '),
    zIndex: 2,
  };

  const btnBase = {
    flex:          1,
    padding:       '10px 8px',
    background:    `linear-gradient(160deg, ${C.btnGoldB} 0%, ${C.btnGoldA} 40%, #a8742e 100%)`,
    border:        '1px solid rgba(212,168,83,0.6)',
    color:         C.btnText,
    fontFamily:    "'Cinzel', serif",
    fontSize:      'clamp(12px, 3.2vw, 15px)',
    fontWeight:    700,
    letterSpacing: '0.8px',
    cursor:        'pointer',
    boxShadow:     '0 2px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,240,160,0.5)',
    transition:    'filter 0.18s ease, transform 0.18s ease',
    borderRadius:  '2px',
    textTransform: 'none',
  };

  /* ── Loading spinner (renders behind animation overlay) ──────────────── */
  const spinner = (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding: 60 }}>
      <div style={{ width:28, height:28, border:`2px solid ${C.glassBorder}`,
                    borderTopColor:'transparent', borderRadius:'50%',
                    animation:'spin 0.8s linear infinite' }} />
    </div>
  );

  return (
    <div style={{
      minHeight:            '100vh',
      backgroundImage:      'url(https://customer-assets.emergentagent.com/job_b4805a07-765d-4b82-bd36-89754495be40/artifacts/wmrlspr0_new%20background%20for%20site%202.png)',
      backgroundSize:       'cover',
      backgroundPosition:   'center center',
      backgroundRepeat:     'no-repeat',
      backgroundAttachment: 'fixed',
      display:              'flex',
      flexDirection:        'column',
      alignItems:           'center',
      justifyContent:       'center',
      padding:              '24px 12px',
      boxSizing:            'border-box',
      position:             'relative',
      overflow:             'hidden',
    }}>

      {/* ── CSS keyframes ─────────────────────────────────────────────────── */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Gold-etch glow: blazes in, then settles to a permanent soft gleam */
        @keyframes inv-etch-glow {
          0%   { filter: drop-shadow(0 0 22px rgba(255,223,60,1.0))
                         drop-shadow(0 0 8px  rgba(255,250,200,0.9)); }
          25%  { filter: drop-shadow(0 0 28px rgba(255,210,40,0.95))
                         drop-shadow(0 0 12px rgba(255,245,180,0.80)); }
          65%  { filter: drop-shadow(0 0 12px rgba(212,168,83,0.55))
                         drop-shadow(0 0 4px  rgba(255,230,140,0.30)); }
          100% { filter: drop-shadow(0 2px 6px rgba(212,168,83,0.28)); }
        }

        /* Subtle card entrance on reveal — transform only, no opacity dip */
        @keyframes inv-card-settle {
          from { transform: scale(0.990) translateY(6px); }
          to   { transform: scale(1)     translateY(0);   }
        }

        /* Gold shimmer sweep for the Birthday script */
        @keyframes inv-script-sweep {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }

      `}</style>

      {/* ── Intro animation overlay ────────────────────────────────────────
          position:fixed/z-index:100 — covers everything underneath.
          The card below is always rendered; it becomes visible when overlay fades.
      ─────────────────────────────────────────────────────────────────────── */}
      {showIntro && (
        <InviteIntroAnimation onComplete={handleAnimationComplete} />
      )}

      {/* ── Outer gold-border card ─────────────────────────────────────── */}
      <div style={{ ...cardStyle, zIndex: 1 }}>
        <div style={innerCardStyle}>
          <div style={glassPanelStyle}>

            {loading ? spinner : (
              <>
                {/* Dear [Name] */}
                {name && (
                  <p style={{
                    fontFamily:    "'Cormorant Garamond', serif",
                    fontSize:      'clamp(15px, 4vw, 20px)',
                    fontStyle:     'italic',
                    fontWeight:    500,
                    textAlign:     'left',
                    margin:        '0 0 14px 0',
                    letterSpacing: '0.3px',
                    ...etchedGold('0.05s'),
                  }}>
                    Dear {name},
                  </p>
                )}

                {/* Body paragraph */}
                <p style={{
                  fontFamily:    "'Cormorant Garamond', serif",
                  fontSize:      'clamp(12px, 3.4vw, 15px)',
                  fontWeight:    400,
                  color:         'rgba(255,255,255,1.0)',
                  textAlign:     'center',
                  lineHeight:    1.7,
                  margin:        '0 0 20px 0',
                  letterSpacing: '0.2px',
                }}>
                  You have been an essential thread in the 80 years of connection Carol
                  Valenciano has built. We would be honored to have you join us for her
                  milestone celebration.
                </p>

                {/* Carol's 80th */}
                <div style={{ textAlign:'center', lineHeight:1.1, margin:'-1em 0 -4px 0' }}>
                  <span style={{
                    fontFamily:    "'Playfair Display', serif",
                    fontSize:      'clamp(26px, 7.2vw, 42px)',
                    fontWeight:    400,
                    letterSpacing: '1px',
                    display:       'inline',
                    ...etchedGold('0.1s'),
                  }}>
                    Carol's{' '}
                  </span>
                  <span style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize:   'clamp(43px, 12vw, 70px)',
                    fontWeight: 700,
                    display:    'inline',
                    ...etchedGold('0.15s'),
                  }}>
                    80
                  </span>
                  <sup style={{
                    fontFamily:    "'Cormorant Garamond', serif",
                    fontSize:      'clamp(17px, 4.2vw, 26px)',
                    fontWeight:    600,
                    verticalAlign: 'super',
                    ...etchedGold('0.18s'),
                  }}>th</sup>
                </div>

                {/* Birthday script — 1 line lower */}
                <div style={{ textAlign:'center', marginTop:'0.8em', marginBottom:22 }}>
                  <span style={{
                    fontFamily:  "'Pinyon Script', cursive",
                    fontSize:    'clamp(48px, 13vw, 76px)',
                    display:     'block',
                    lineHeight:  1.1,
                    ...(playShimmer ? {
                      background: 'linear-gradient(90deg,' +
                        '#A87820 0%,#D4A82F 12%,' +
                        '#F8E060 28%,#FFFBE0 45%,' +
                        '#F8E060 62%,#D4A82F 78%,' +
                        '#A87820 100%)',
                      backgroundSize:       '300% auto',
                      WebkitBackgroundClip: 'text',
                      backgroundClip:       'text',
                      WebkitTextFillColor:  'transparent',
                      color:                'transparent',
                      animation:            'inv-etch-glow 3.2s ease-out 0.22s 1 both, inv-script-sweep 2.6s ease-in-out 0.22s 1 both',
                    } : {
                      color:  C.textPrimary,
                      filter: 'drop-shadow(0 2px 8px rgba(212,168,83,0.35))',
                    }),
                  }}>
                    Birthday
                  </span>
                </div>

                {/* Gold divider */}
                <div style={{
                  height:     '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(255,220,90,0.98), rgba(255,240,140,1.0), rgba(255,220,90,0.98), transparent)',
                  margin:     '0 0 18px 0',
                  boxShadow:  '0 0 3px rgba(255,210,60,0.35)',
                }} />

                {/* Buttons */}
                <div style={{ display:'flex', gap:10, marginBottom:20 }}>
                  <button
                    data-testid="btn-view-invitation"
                    style={btnBase}
                    onClick={handleViewInvitation}
                    onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.12)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.filter=''; e.currentTarget.style.transform=''; }}
                  >
                    View Official Invitation
                  </button>
                  <button
                    data-testid="btn-rsvp-now"
                    style={btnBase}
                    onClick={handleRsvpNow}
                    onMouseEnter={e => { e.currentTarget.style.filter='brightness(1.12)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.filter=''; e.currentTarget.style.transform=''; }}
                  >
                    RSVP Now
                  </button>
                </div>

                {/* Event details */}
                <div style={{ textAlign:'center' }}>
                  <p style={{
                    fontFamily:    "'Cinzel', serif",
                    fontSize:      'clamp(10px, 2.8vw, 13px)',
                    fontWeight:    600,
                    color:         C.textSoft,
                    letterSpacing: '2px',
                    margin:        '0 0 4px 0',
                  }}>
                    {dateStr}
                  </p>
                  <p style={{
                    fontFamily:    "'Cinzel', serif",
                    fontSize:      'clamp(12px, 3.2vw, 16px)',
                    fontWeight:    700,
                    color:         C.textPrimary,
                    letterSpacing: '3px',
                    margin:        '0 0 10px 0',
                  }}>
                    {timeStr}
                  </p>
                  <div style={{
                    height:     '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(255,220,90,0.98), rgba(255,240,140,1.0), rgba(255,220,90,0.98), transparent)',
                    margin:     '0 0 10px 0',
                    boxShadow:  '0 0 3px rgba(255,210,60,0.35)',
                  }} />
                  <p style={{
                    fontFamily:    "'Cinzel', serif",
                    fontSize:      'clamp(9px, 2.5vw, 12px)',
                    fontWeight:    700,
                    color:         C.textSoft,
                    letterSpacing: '2.5px',
                    textTransform: 'uppercase',
                    margin:        '0 0 3px 0',
                  }}>
                    {venueName}
                  </p>
                  <p style={{
                    fontFamily:    "'Cinzel', serif",
                    fontSize:      'clamp(7px, 1.9vw, 10px)',
                    fontWeight:    400,
                    color:         C.textFaint,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    margin:        0,
                  }}>
                    {venueAddr}
                  </p>
                </div>

              </>
            )}

          </div>{/* /glass panel */}
        </div>{/* /inner card */}
      </div>{/* /outer border */}

    </div>
  );
}
